const FULL_MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/**
 * Get the full month name for a date (e.g. "March").
 * @param date - Optional date; defaults to current date
 */
export const getMonthName = (date: Date = new Date()): string => {
  return FULL_MONTH_NAMES[date.getMonth()];
};

/**
 * Format date as "Month D, YYYY" (e.g. "January 15, 2024").
 * Returns "—" for empty or invalid input.
 */
export const formatLongDate = (dateString?: string | null): string => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "—";
  return `${getMonthName(date)} ${date.getDate()}, ${date.getFullYear()}`;
};

export const formatDate = (dateString?: string): string => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return dateString;
  }
};

/**
 * Format date as "day month, year" with abbreviated month (e.g., "3 Aug, 2025")
 */
export const formatDateWithAbbreviatedMonth = (dateString?: string): string => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    const day = date.getDate();
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month}, ${year}`;
  } catch {
    return dateString;
  }
};

/**
 * Get current year's January 1st as minimum date (YYYY-MM-DD format)
 */
export const getCurrentYearMinDate = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  return `${year}-01-01`;
};

/**
 * Get next year's December 31st as maximum date (YYYY-MM-DD format)
 */
export const getNextYearMaxDate = (): string => {
  const now = new Date();
  const nextYear = now.getFullYear() + 1;
  return `${nextYear}-12-31`;
};

/**
 * Get next year's December 30th as maximum date for policy end date
 * (certificates expire the day before, so policy end date should be one day before expiry)
 */
export const getPolicyEndDateMax = (): string => {
  const now = new Date();
  const nextYear = now.getFullYear() + 1;
  const date = new Date(nextYear, 11, 31); // December 31st
  date.setDate(date.getDate() - 1); // One day before
  return date.toISOString().split("T")[0];
};

/**
 * Calculate expiry date (one day before one year from commencement date)
 * Insurance certificates expire the day before, so policy end date should be one day before the one-year mark
 */
export const calculateExpiryDate = (commencementDate: string): string => {
  if (!commencementDate) return "";
  const date = new Date(commencementDate);
  date.setFullYear(date.getFullYear() + 1);
  date.setDate(date.getDate() - 1); // One day before (certificates expire the day before)
  return date.toISOString().split("T")[0];
};

/**
 * Format a timestamp as a short relative time string (e.g. "2h ago", "Just now").
 * Falls back to `formatDateWithAbbreviatedMonth` beyond 7 days.
 */
export const formatRelativeTime = (dateString?: string | null): string => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";

  const diffMs = Date.now() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  if (diffSeconds < 60) return "Just now";

  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) return `${diffMinutes}m ago`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;

  return formatDateWithAbbreviatedMonth(dateString);
};

/**
 * Format a timestamp as "3 Aug, 2025, 14:32" for message/thread display.
 */
export const formatDateTime = (dateString?: string | null): string => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";
  const time = date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${formatDateWithAbbreviatedMonth(dateString)}, ${time}`;
};

/**
 * Format a timestamp as a short time string for chat bubbles (e.g. "9:16 AM").
 */
export const formatTimeOnly = (dateString?: string | null): string => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
};

/**
 * Format a timestamp as a chat-thread day divider label (e.g. "Today", "Yesterday", "3 Aug, 2025").
 */
export const formatDayDivider = (dateString?: string | null): string => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";

  const startOfDay = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diffDays = Math.round(
    (startOfDay(new Date()) - startOfDay(date)) / (24 * 60 * 60 * 1000)
  );

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  return formatDateWithAbbreviatedMonth(dateString);
};
